import React, { useEffect, useState } from 'react';
import {
  Box, Typography, IconButton, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import ClinicalHtmlEditor from './ClinicalHtmlEditor';

export type DocumentTextTemplateCategory = 'report' | 'surgical_risk' | 'evolution';

export interface DocumentTextTemplateFormDialogProps {
  open: boolean;
  onClose: () => void;
  category: DocumentTextTemplateCategory;
  professionalId?: string | null;
  editing?: any | null;
  onSaved: () => void;
}

const CATEGORY_LABEL: Record<DocumentTextTemplateCategory, string> = {
  report: 'relatório médico',
  evolution: 'evolução',
  surgical_risk: 'risco cirúrgico',
};

const DocumentTextTemplateFormDialog: React.FC<DocumentTextTemplateFormDialogProps> = ({
  open,
  onClose,
  category,
  professionalId,
  editing,
  onSaved,
}) => {
  const [name, setName] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [scope, setScope] = useState<'clinic' | 'professional'>('clinic');
  const [specialty, setSpecialty] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const label = CATEGORY_LABEL[category];
  const isEdit = Boolean(editing?.id);

  useEffect(() => {
    if (!open) return;
    if (editing?.id) {
      setName(editing.name || '');
      setBodyText(editing.body_text || '');
      setScope(editing.scope === 'professional' ? 'professional' : 'clinic');
      setSpecialty(editing.specialty || '');
    } else {
      setName('');
      setBodyText('');
      setScope('clinic');
      setSpecialty('');
    }
    setError('');
  }, [open, editing]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Informe um título para o modelo.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const payload = {
        category,
        name: name.trim(),
        body_text: bodyText,
        scope,
        professional_id: scope === 'professional' ? professionalId || null : null,
        specialty: specialty.trim() || null,
      };
      if (isEdit) {
        await axios.put(getApiUrl(`/clinic/document-text-templates/${editing.id}`), payload);
      } else {
        await axios.post(getApiUrl('/clinic/document-text-templates'), payload);
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao salvar modelo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {isEdit ? 'Editar modelo' : 'Novo modelo'} de {label}
        </Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          fullWidth
          size="small"
          label="Título *"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2 }}
          autoFocus
        />
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Disponibilidade</InputLabel>
          <Select value={scope} label="Disponibilidade" onChange={(e) => setScope(e.target.value as 'clinic' | 'professional')}>
            <MenuItem value="clinic">Toda a clínica (todos os profissionais)</MenuItem>
            <MenuItem value="professional" disabled={!professionalId}>Somente eu (meu modelo)</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          size="small"
          label="Aplicabilidade (especialidade)"
          value={specialty}
          onChange={(e) => setSpecialty(e.target.value)}
          placeholder="Opcional — ex.: Oftalmologia. Vazio = todas as especialidades."
          sx={{ mb: 2 }}
        />
        <ClinicalHtmlEditor
          label="Texto do modelo"
          minRows={8}
          placeholder={
            category === 'evolution'
              ? 'Texto pré-preenchido da evolução. Data, profissional e identificação do paciente são automáticos.'
              : 'Texto livre pré-preenchido. Identificação, data e rodapé (timbrado) são automáticos na impressão.'
          }
          value={bodyText}
          onChange={setBodyText}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
        >
          Salvar modelo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentTextTemplateFormDialog;
